/* eslint-disable react/prop-types */
/**
 * FloorView — Task 1a landing screen for /super/pos/terminal.
 *
 * A cashier arrives here, not on the billing screen. The floor is
 * where dine-in service starts. Non-dine-in orders are started from
 * a clearly separate control (top-right buttons) so they never route
 * through a table.
 *
 * Table tiles derive their state from the orders in the store (per
 * Contract §5). No status field on the table object.
 *
 * Sized so a 40-table outlet fits at 1366×768 without vertical
 * scrolling: 8 columns × 5 rows of ~144×88 tiles = ≤720px vertical
 * inside a ≤540px grid region, easily achievable at this viewport.
 */
import React, { useEffect, useRef, useState } from "react";
import { ShoppingBag, Bike, Building, Wifi, WifiOff, Pause, Sparkles, Ban, FileMinus, Inbox,
  Sun, Sunset, ReceiptText, Utensils, Coffee, Monitor, Lock, LogOut, ArrowLeft } from "lucide-react";
import { TABLES, ZONES, MOCK_AGGREGATOR_ORDERS, AGGREGATOR_ESCALATION_MS } from "./mockData";
import { useCustomerBank, normalisePhone } from "./useCustomerBank";
import { ACT, ORDER_TYPE, ORDER_STATE, tableState, tableUnavailableReason, activeOrderForTable, heldOrders, computeTotals, computeTotalsFor, getActive, GRACE_MS, unsettledQueue, staffNameFor } from "./posReducer";
import ReasonInput, { UNAVAILABLE_PRESETS, normaliseReason, reasonValid } from "./ReasonInput";
import { AlphaKeyboard, applyKeyToValue } from "./OnScreenKeyboard";
import BillPreviewDialog from "./BillPreviewDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  BusinessDayBanner, BusinessDayOpenDialog, BusinessDayCloseDialog,
  XReportDialog, ZReportDialog,
  ReportsHub, BillRegisterReport, VoidDiscountAuditReport, ItemSalesReport,
  HourlySalesReport, TaxSummaryReport, SettlementSummaryReport,
  KitchenDisplay, TokenQueue, TicketSlipDialog,
  isBusinessDayOpen, openShifts,
  aggregateSales, aggregateSettlements,
} from "./Sprint3";
import { ExceptionReport, ManagerPinDialog as SharedPinDialog } from "./Sprint4";
import QuantityElevationDialog from "./QuantityElevationDialog";
import { POS_CONFIG } from "./posConfig";
import TillBadge from "./TillBadge";
import { UnsettledQueuePanel, UnsettledDayCloseBlocker } from "./UnsettledQueue";
import { TillShiftCloseFlow } from "./TillShiftCloseFlow";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

export default function FloorView({ state, dispatch, livePos, outletName, userName, onLock, onCloseTill, onOpenTill, onLogout, onExit }) {
  const [zone, setZone] = useState(ZONES[0].id);
  const [heldTrayOpen, setHeldTrayOpen] = useState(false);
  const [voidedListOpen, setVoidedListOpen] = useState(false); // Part 11 · voided invoices view
  const [voidedBillOrderId, setVoidedBillOrderId] = useState(null); // Part 7.2 · viewable voided bill
  const [aggregatorTrayOpen, setAggregatorTrayOpen] = useState(false); // Part 6 · aggregator inbox
  const [aggregatorRejectFor, setAggregatorRejectFor] = useState(null); // agg order pending rejection reason
  const [unavailDialogFor, setUnavailDialogFor] = useState(null); // Part 3+5 · reason dialog
  const [openDialogFor, setOpenDialogFor] = useState(null); // vacant-table dialog target
  // Part 4 · Manage-unavailable mode (touchscreen-friendly alternative to right-click)
  const [manageUnavail, setManageUnavail] = useState(false);
  // Part 9 · temp-table naming dialog
  const [tempDialogOpen, setTempDialogOpen] = useState(false);
  // ── Sprint 3 · Business day + shifts + reports + KDS + token queue
  const [bizOpenDlg, setBizOpenDlg] = useState(false);
  const [bizCloseDlg, setBizCloseDlg] = useState(false);
  // POS Cleanup Pass 3 · Close Day drives the till → shift → day
  // termination sequence. `dayCloseFlowOpen` mounts TillShiftCloseFlow
  // as an orchestrator; on its `onShiftClosed` we chain closeBusinessDay
  // and openBusinessDay(next_business_date) so the terminal lands on
  // the rolled-forward date and session-status returns it after a
  // refresh. There is no user-facing Open Shift / Close Shift button
  // anymore — shifts auto-open on PIN login and auto-close here.
  const [dayCloseFlowOpen, setDayCloseFlowOpen] = useState(false);
  // POS Control Sprint · generic PIN-capture pipe. `TillShiftCloseFlow`
  // (and any future caller) can request a manager PIN by calling
  // `requestAuth(reason, onDone)`. We render a single shared
  // SharedPinDialog against this state so we never duplicate the PIN
  // UX at each call site.
  const [authRequest, setAuthRequest] = useState(null);
  const requestAuth = (reason, done) => setAuthRequest({ reason, done });
  const [xReportOpen, setXReportOpen] = useState(false);
  const [zReportEntry, setZReportEntry] = useState(null); // {}
  const [reportsHubOpen, setReportsHubOpen] = useState(false);
  const [operationalReport, setOperationalReport] = useState(null); // "bill_register" etc
  const [kdsOpen, setKdsOpen] = useState(false);
  const [tokenQueueOpen, setTokenQueueOpen] = useState(false);
  // Sprint 2 Part C · unsettled orders queue (takeaway + delivery
  // that have fired at least one ticket but aren't settled yet)
  const [unsettledOpen, setUnsettledOpen] = useState(false);
  const [dayCloseBlocker, setDayCloseBlocker] = useState(false);
  // Sprint 5 correction 6 · Guest-facing display is a separate view of
  // the same token queue — token number + first name only, huge type,
  // safe to project to a public monitor. Kept as a distinct dialog
  // instance so it can be opened alongside the cashier view.
  const [guestDisplayOpen, setGuestDisplayOpen] = useState(false);
  const [tokenGuestOpen, setTokenGuestOpen] = useState(false);
  const [bizPromptFor, setBizPromptFor] = useState(null); // pending order type to open after biz day opens
  // Sprint 4 correction 6 · when the user picks a closed date, we open
  // a Manager-PIN dialog before firing the reopen override.
  const [reopenPinFor, setReopenPinFor] = useState(null); // { business_date }
  // Financial Ground Truth (Feb 2026) · Move-to-dues manager auth for
  // an unpaid printed invoice at business-day close. The row-level
  // action from the day-close blocker sets this; the SharedPinDialog
  // instance below reads it and drives the server call.
  const [dueTransferFor, setDueTransferFor] = useState(null); // unsettledQueue row
  const held = heldOrders(state);
  const tempTables = state.session.temp_tables || [];
  const bizOpen = isBusinessDayOpen(state);
  const currentShift = openShifts(state)[0] || null;
  // Merge fixed + temp tables. Temp tables always show in the temp zone strip.
  const zoneTables = zone === "temp"
    ? tempTables.map((t) => ({ id: t.id, label: t.label, capacity: null, zone: "temp", is_temp: true }))
    : TABLES.filter((t) => t.zone === zone);

  // Force a periodic re-render so the "elapsed since opened_at" tag
  // and the 120s grace countdown stay fresh. 5s cadence so the grace
  // dot lapses visibly.
  const [, tick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(iv);
  }, []);

  const startNonDineIn = (type) => {
    // Part 6 · refuse ordering when the business day is closed. Prompt to open.
    if (!bizOpen) { setBizPromptFor({ kind: "non_dine_in", type }); return; }
    // POS Cleanup Pass 1 · every LIVE-mode call must reach the server
    // and receive a real order id. Reducer fallback is DEMO ONLY.
    // Historically the guard was `if (livePos)` — but useLivePos
    // returns an object regardless of live-mode, so real /pos was
    // silently minting local uid("ord") orders after settlement.
    if (livePos?.isLive) { livePos.openOrder({ order_type: type }); return; }
    dispatch({ type: ACT.ORDER_OPENED, payload: { type } });
  };

  const onTileTap = (table) => {
    if (manageUnavail) {
      setUnavailDialogFor(table);
      return;
    }
    const st = tableState(state, table.id);
    if (st === "VACANT" || st === "GRACE") {
      // Part 6 · refuse ordering when the business day is closed.
      if (!bizOpen) { setBizPromptFor({ kind: "table", table }); return; }
      setOpenDialogFor(table);
    } else if (st === "OCCUPIED" || st === "BILLED") {
      const order = activeOrderForTable(state, table.id);
      if (order) dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id: order.order.id } });
    } else if (st === "UNAVAILABLE") {
      setUnavailDialogFor(table);
    }
  };

  // Part 4 · long-press (500ms) → mark unavailable on the touched tile.
  // Works with a single finger, no right-click needed.
  const longPressRef = useRef({ table: null, timer: null, moved: false });
  const onTilePointerDown = (table, e) => {
    if (manageUnavail) return; // already in manage mode
    longPressRef.current.table = table;
    longPressRef.current.moved = false;
    longPressRef.current.timer = setTimeout(() => {
      setUnavailDialogFor(table);
      longPressRef.current.table = null;
    }, 500);
  };
  const clearLongPress = () => {
    if (longPressRef.current.timer) clearTimeout(longPressRef.current.timer);
    longPressRef.current.timer = null;
  };

  return (
    <div className="h-full w-full bg-[color:var(--pos-color-page-bg,#F4F6F2)] flex flex-col overflow-hidden"
      data-testid="pos-floor-view">
      {/* ─── Top bar ─── Part 8.1 (Sprint 4) · three balanced zones.
          Left: order context. Centre: outlet + user. Right: terminal
          controls. Nothing overlapping, nothing compressed. */}
      <div className="h-11 shrink-0 bg-[#1A2421] text-white grid grid-cols-[1fr_auto_1fr] items-center px-3 gap-3">
        {/* Left · order context */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[12px] font-semibold shrink-0">RestroPulse POS · Floor</div>
          <div className="flex items-center gap-1.5 text-[10px] shrink-0">
            {state.connection.online
              ? <Wifi className="w-3.5 h-3.5 text-emerald-400" />
              : <WifiOff className="w-3.5 h-3.5 text-amber-400" />}
            <span>{state.connection.online ? "Online" : "Offline"}</span>
          </div>
        </div>

        {/* Centre · outlet + user + till */}
        <div className="flex items-center gap-3 justify-center min-w-0">
          <div className="text-[11px] text-white/85 truncate max-w-[280px]" title={outletName}>{outletName}</div>
          <div className="w-px h-4 bg-white/20" />
          <div className="text-[10px] text-white/70 whitespace-nowrap">User: <strong className="text-white">{userName}</strong></div>
          {/* Sprint 2 B.2 · Till chip — must be UNMISTAKABLE. Green with
              float + cashier name when open; red "No till open" when
              not. Sits next to the User label so the top bar makes it
              impossible to mistake whose till is receiving cash. Amber
              variant kicks in past the tenant duration threshold. */}
          <TillBadge state={state} userName={userName} theme="dark" />
        </div>

        {/* Right · terminal controls */}
        <div className="flex items-center gap-1 2xl:gap-1.5 justify-end min-w-0 overflow-hidden">
          <button type="button"
            onClick={() => setHeldTrayOpen(true)}
            title="Held orders"
            aria-label="Held orders"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 relative shrink-0"
            data-testid="pos-held-tray-open">
            <Pause className="w-4 h-4" /><span className="hidden 2xl:inline">Held</span>
            {held.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-sky-500 text-white text-[10px] font-semibold tabular-nums"
                data-testid="pos-held-tray-count">{held.length}</span>
            )}
          </button>
          {/* Sprint 2 Part C · unsettled orders queue — takeaway +
              delivery orders that owe money. Count badge advances the
              moment the first ticket fires; entry stays through print
              until settlement. Distinct from the token queue. */}
          <button type="button"
            onClick={() => setUnsettledOpen(true)}
            title="Open orders — takeaway/delivery not yet settled"
            aria-label="Open orders"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 relative shrink-0"
            data-testid="pos-unsettled-queue-open">
            <ReceiptText className="w-4 h-4" /><span className="hidden 2xl:inline">Open orders</span>
            {(() => {
              const n = unsettledQueue(state).length;
              return n > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold tabular-nums"
                  data-testid="pos-unsettled-queue-count-badge">{n}</span>
              ) : null;
            })()}
          </button>
          <button type="button"
            onClick={() => setVoidedListOpen(true)}
            title="Voided invoices"
            aria-label="Voided invoices"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 relative shrink-0"
            data-testid="pos-voided-invoices-open">
            <FileMinus className="w-4 h-4" /><span className="hidden 2xl:inline">Voided</span>
            {(state.session.invoice_voids || []).length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold tabular-nums"
                data-testid="pos-voided-invoices-count">{(state.session.invoice_voids || []).length}</span>
            )}
          </button>
          <button type="button"
            onClick={() => setAggregatorTrayOpen(true)}
            title="Incoming aggregator orders"
            aria-label="Incoming aggregator orders"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 relative shrink-0"
            data-testid="pos-aggregator-open">
            <Inbox className="w-4 h-4" /><span className="hidden 2xl:inline">Incoming</span>
            {(() => {
              const processed = state.session.aggregator_processed || {};
              const pending = MOCK_AGGREGATOR_ORDERS.filter((a) => !processed[a.id]).length;
              return pending > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-semibold tabular-nums"
                  data-testid="pos-aggregator-count">{pending}</span>
              ) : null;
            })()}
          </button>
          {/* Reports button moved to bottom strip (Financial Ground
              Truth · Feb 2026). Kept the modal state above so the
              bottom-strip trigger can still open it. */}
          {POS_CONFIG.kds_enabled && (
            <button type="button" onClick={() => setKdsOpen(true)}
              title="Kitchen Display System"
              aria-label="Kitchen Display System"
              className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 shrink-0"
              data-testid="pos-kds-open">
              <Monitor className="w-4 h-4" /><span className="hidden 2xl:inline">KDS</span>
            </button>
          )}
          <button type="button" onClick={() => setTokenQueueOpen(true)}
            title="Token queue"
            aria-label="Token queue"
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 relative shrink-0"
            data-testid="pos-token-queue-open">
            <Coffee className="w-4 h-4" /><span className="hidden 2xl:inline">Tokens</span>
            {(() => {
              const n = (state.session.tokens || []).filter((t) => t.state !== "collected").length;
              return n > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-500 text-white text-[10px] font-semibold tabular-nums"
                  data-testid="pos-token-queue-count">{n}</span>
              ) : null;
            })()}
          </button>
          {/* Sprint 5 correction 6 · Guest display · public-safe view */}
          <button type="button" onClick={() => setGuestDisplayOpen(true)}
            className="inline-flex items-center gap-1.5 h-11 min-w-[44px] px-2 2xl:px-3 rounded border border-white/20 text-[12px] hover:bg-white/10 shrink-0"
            data-testid="pos-token-guest-display-open"
            title="Open the public-facing token display (token number + first name only)"
            aria-label="Guest display">
            <Monitor className="w-4 h-4" /><span className="hidden 2xl:inline">Guest display</span>
          </button>
          {/* Financial Ground Truth (Feb 2026) · Reports, Lock, Log out
              and Exit have moved to the persistent bottom strip so the
              top strip carries only operationally-frequent controls
              (outlet + user + till + tokens + open orders + held +
              incoming). This keeps the top bar readable at 15" POS
              resolutions and prevents the badge / till chip overlap
              reported in the last sprint. See the `pos-bottom-strip`
              region below the floor grid. */}
        </div>
      </div>

      {/* ─── Sprint 3 · Business day banner ─── */}
      <BusinessDayBanner state={state} />

      {/* ─── Non-dine-in row + Part 9 temp table ─── */}
      <div className="h-12 shrink-0 bg-white border-b border-[color:var(--pos-color-border,#E2E8DF)] px-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-medium">Start order</div>
          <NonDineInBtn label="New takeaway"    icon={ShoppingBag} testid="pos-new-takeaway"
            onClick={() => startNonDineIn(ORDER_TYPE.TAKEAWAY)} />
          <NonDineInBtn label="New delivery"    icon={Bike}         testid="pos-new-delivery"
            onClick={() => startNonDineIn(ORDER_TYPE.DELIVERY)} />
          <NonDineInBtn label="New room service" icon={Building}   testid="pos-new-room-service"
            onClick={() => startNonDineIn(ORDER_TYPE.ROOM_SERVICE)} />
          <NonDineInBtn label="New temp table" icon={Sparkles}     testid="pos-new-temp-table"
            onClick={() => setTempDialogOpen(true)} />
        </div>
        <div className="flex items-center gap-2">
          {/* Manage tables (renamed from "Manage availability", Financial
              Ground Truth · Feb 2026) moved to the bottom strip.
              The mode toggle here surfaces its exit state so an admin
              who taps a tile stays in-mode until they explicitly leave. */}
          {manageUnavail && (
            <button type="button"
              onClick={() => setManageUnavail(false)}
              data-testid="pos-manage-tables-done"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded border text-[12px] font-medium
                border-slate-700 bg-slate-700 text-white">
              <Ban className="w-3.5 h-3.5" /> Done managing
            </button>
          )}
          {/* Sprint 4 correction 4 · The floor-level "Open item"
              checkbox has been removed. Open items are now created
              via the "Open items" category in the menu area of the
              billing screen, gated by manager PIN, and persist for
              the business day. */}
        </div>
      </div>

      {/* Manage-unavailable banner */}
      {manageUnavail && (
        <div className="bg-slate-700 text-white px-3 py-1.5 text-[11px] flex items-center justify-between"
          data-testid="pos-manage-unavail-banner">
          <span>Tap any tile to set or change its availability reason. Long-press works the same way outside this mode.</span>
          <button onClick={() => setManageUnavail(false)}
            className="text-[10px] underline underline-offset-2"
            data-testid="pos-manage-unavail-exit">Exit</button>
        </div>
      )}

      {/* ─── Zone tabs (Part 9 · adds Temp when temp tables exist) ─── */}
      <div className="h-11 shrink-0 bg-white border-b border-[color:var(--pos-color-border,#E2E8DF)] px-3 flex items-center gap-1.5"
        data-testid="pos-zone-tabs">
        {[...ZONES, ...(tempTables.length > 0 ? [{ id: "temp", label: `Temp · ${tempTables.length}` }] : [])].map((z) => (
          <button key={z.id} type="button" onClick={() => setZone(z.id)}
            data-testid={`pos-zone-${z.id}`}
            className={`h-8 px-3 rounded-full border text-[12px] font-medium transition
              ${zone === z.id
                ? "border-[color:var(--pos-color-accent,var(--color-primary))] bg-[color:var(--pos-color-selected)] text-[color:var(--pos-color-on-selected,#1A2421)]"
                : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
            {z.label}
          </button>
        ))}
        <div className="flex-1" />
        <StateLegend />
      </div>

      {/* ─── Table grid — CSS Grid with fixed square tiles.
          Part 1.2 · tile size chosen 112px so a tile can legibly
          render five facts simultaneously (code, capacity, covers,
          elapsed time, running total) with a two-letter steward
          initial. At 1366×768 the container is ≈1340 wide × ≈560
          tall. Ten 112px tiles + nine 12px gaps = 1228px, room to
          spare. Four rows of ten = 40 tiles above the fold. */}
      <div className="flex-1 overflow-auto p-4" data-testid="pos-table-grid">
        <div className="grid gap-3 justify-start content-start"
             style={{ gridTemplateColumns: "repeat(auto-fill, 112px)", gridAutoRows: "112px" }}>
          {zoneTables.map((t) => (
            <TableTile key={t.id}
              table={t}
              state={tableState(state, t.id)}
              fullState={state}
              unavailReason={tableUnavailableReason(state, t.id)}
              order={activeOrderForTable(state, t.id)}
              graceEntry={state.session.dirty_tables.find((d) => d.table_id === t.id)}
              manageMode={manageUnavail}
              onTap={() => onTileTap(t)}
              onPointerDown={(e) => onTilePointerDown(t, e)}
              onPointerUp={clearLongPress}
              onPointerLeave={clearLongPress}
              totals={(() => {
                // Sprint 2 Consolidation Part 4 · prefer server-authoritative
                // totals (populated in state.orders[id].order.server_totals
                // by LIVE_ORDER_HYDRATED / ORDER_SYNCED_FROM_SERVER) so the
                // tile figure ALWAYS matches what the server would bill.
                // Field-name alignment: the tile reads `grand_total`,
                // NOT `total` — the previous shim silently produced ₹0
                // because it returned `{ total: ... }` when the tile
                // read `totals.grand_total`.
                const slice = activeOrderForTable(state, t.id);
                if (!slice) return null;
                const s = slice.order.server_totals;
                if (s) return {
                  subtotal:    Number(s.subtotal) || 0,
                  tax:         Number(s.tax_total) || 0,
                  discount:    Number(s.discount_total) || 0,
                  total:       Number(s.grand_total) || 0,
                  grand_total: Number(s.grand_total) || 0,
                };
                const clientTotals = computeTotalsFor(state, slice.order.id);
                if (!clientTotals) return null;
                return { ...clientTotals, grand_total: clientTotals.total };
              })()} />
          ))}
        </div>
      </div>

      {/* ─── Bottom action strip · POS Cleanup Pass 3 · Shift open /
          Close shift / Blind close have been removed. Shifts are
          internal now: they auto-open on the first PIN login of a
          business day and auto-close inside the Close Day flow. */}
      <div className="h-11 shrink-0 bg-white border-t border-[color:var(--pos-color-border,#E2E8DF)] px-3 flex items-center gap-2"
        data-testid="pos-bottom-controls">
        {!bizOpen
          ? (
            <button type="button" onClick={() => setBizOpenDlg(true)}
              data-testid="pos-open-business-day"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded bg-emerald-700 text-white text-[12px] font-semibold hover:bg-emerald-800">
              <Sun className="w-3.5 h-3.5" /> Open business day
            </button>
          )
          : (
            <>
              <button type="button"
                onClick={() => {
                  if (unsettledQueue(state).length > 0) {
                    setDayCloseBlocker(true);
                  } else {
                    setBizCloseDlg(true);
                  }
                }}
                data-testid="pos-close-business-day"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-amber-400 text-amber-800 text-[12px] font-semibold hover:bg-amber-50">
                <Sunset className="w-3.5 h-3.5" /> Close day
              </button>
              {state.session.current_till && onCloseTill && (
                <button type="button" onClick={onCloseTill}
                  data-testid="pos-close-till"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-emerald-500 text-emerald-800 text-[12px] font-semibold hover:bg-emerald-50 whitespace-nowrap">
                  Close till
                </button>
              )}
              {!state.session.current_till && onOpenTill && (() => {
                const role = state.session?.staff_snapshot?.role;
                const CAN_SETTLE = { cashier: true, supervisor: true, manager: true, outlet_admin: true };
                if (!role || !CAN_SETTLE[role]) return null;
                return (
                  <button type="button" onClick={onOpenTill}
                    data-testid="pos-open-till"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-emerald-600 bg-emerald-600 text-white text-[12px] font-semibold hover:bg-emerald-700 whitespace-nowrap">
                    Open till
                  </button>
                );
              })()}
              <button type="button" onClick={() => setXReportOpen(true)}
                data-testid="pos-take-x-report"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
                X report
              </button>
            </>
          )
        }
        {/* Financial Ground Truth (Feb 2026) · secondary controls moved
            from the top strip to this bottom row so the top bar carries
            only the frequently-used operational actions (outlet, user,
            till, tokens, held, open orders). Placed on the RIGHT with
            a divider so day/shift controls on the left stay grouped. */}
        <div className="ml-auto flex items-center gap-1.5" data-testid="pos-bottom-strip-secondary">
          <button type="button"
            onClick={() => setManageUnavail(!manageUnavail)}
            data-testid="pos-manage-tables-toggle"
            title="Manage tables · mark unavailable, temp tables"
            aria-label="Manage tables"
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded border text-[11px] font-medium
              ${manageUnavail
                ? "border-slate-700 bg-slate-700 text-white"
                : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
            <Ban className="w-3.5 h-3.5" />
            <span className="hidden md:inline">{manageUnavail ? "Done" : "Manage tables"}</span>
          </button>
          <div className="w-px h-5 bg-[#E2E8DF] mx-0.5" />
          <button type="button"
            onClick={() => setReportsHubOpen(true)}
            data-testid="pos-reports-open-bottom"
            title="Reports"
            aria-label="Reports"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
            <ReceiptText className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Reports</span>
          </button>
          {onLock && (
            <button type="button" onClick={onLock}
              data-testid="pos-lock-terminal"
              title="Lock the terminal · shift stays open"
              aria-label="Lock terminal"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
              <Lock className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Lock</span>
            </button>
          )}
          <button type="button"
            onClick={() => (onLogout ? onLogout() : dispatch({ type: ACT.STAFF_LOGGED_OUT }))}
            data-testid="pos-log-out"
            title="Log out · reconciles your till first"
            aria-label="Log out"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Log out</span>
          </button>
          {onExit && (
            <button type="button" onClick={onExit}
              data-testid="pos-exit-terminal"
              title="Exit terminal · leave POS"
              aria-label="Exit terminal"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
              <ArrowLeft className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Exit</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Table-open dialog ─── */}
      {openDialogFor && (
        <TableOpenDialog
          table={openDialogFor}
          onCancel={() => setOpenDialogFor(null)}
          onConfirm={(payload) => {
            if (livePos) livePos.openTable({ table_id: openDialogFor.id, ...payload });
            else dispatch({ type: ACT.TABLE_OPENED, payload: { table_id: openDialogFor.id, ...payload } });
            setOpenDialogFor(null);
          }} />
      )}

      {/* ─── Unavailable dialog · Part 3+5 ─── */}
      {unavailDialogFor && (
        <UnavailableDialog
          table={unavailDialogFor}
          currentReason={tableUnavailableReason(state, unavailDialogFor.id)}
          onCancel={() => setUnavailDialogFor(null)}
          onMark={(payload) => {
            dispatch({ type: ACT.TABLE_MARKED_UNAVAILABLE, payload: {
              table_id: unavailDialogFor.id, reason_code: payload.reason_code, note: payload.note,
            }});
            setUnavailDialogFor(null);
          }}
          onRestore={() => {
            dispatch({ type: ACT.TABLE_MADE_AVAILABLE, payload: { table_id: unavailDialogFor.id } });
            setUnavailDialogFor(null);
          }} />
      )}

      {/* ─── Temp table dialog · Part 9 ─── */}
      <TempTableDialog
        open={tempDialogOpen}
        onCancel={() => setTempDialogOpen(false)}
        onCreate={(label) => {
          dispatch({ type: ACT.TEMP_TABLE_CREATED, payload: { label } });
          setTempDialogOpen(false);
          setZone("temp"); // jump to the temp strip so the new tile is visible
        }} />

      {/* ─── Held-orders tray ─── */}
      {heldTrayOpen && (
        <HeldOrdersTray
          held={held}
          onClose={() => setHeldTrayOpen(false)}
          onResume={(order_id) => {
            dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id } });
            if (livePos) livePos.resumeActive(order_id);
            else dispatch({ type: ACT.ORDER_RESUMED });
            setHeldTrayOpen(false);
          }} />
      )}

      {/* ─── Sprint 2 Part C · Unsettled orders queue ─── */}
      <UnsettledQueuePanel
        state={state}
        open={unsettledOpen}
        onClose={() => setUnsettledOpen(false)}
        onResume={(order_id) => {
          // Activating the slice lands the cashier back on billing
          // with the exact state — same lines, same tickets, same
          // guest info. Held-state orders also need a resume dispatch
          // so the reducer flips them back to OPEN before edits.
          const slice = state.orders?.[order_id];
          if (slice?.order?.state === ORDER_STATE.HELD) {
            if (livePos) livePos.resumeActive(order_id);
            else dispatch({ type: ACT.ORDER_RESUMED });
          }
          dispatch({ type: ACT.ORDER_ACTIVATED, payload: { order_id } });
        }} />

      {/* ─── Part C · Day-close blocker ─── */}
      <UnsettledDayCloseBlocker
        state={state}
        open={dayCloseBlocker}
        livePos={livePos}
        onClose={() => setDayCloseBlocker(false)}
        onGoToQueue={() => { setDayCloseBlocker(false); setUnsettledOpen(true); }}
        onMoveToDues={(entry) => {
          // Financial Ground Truth (Feb 2026) · manager auth captured
          // via SharedPinDialog; reason typed inline. Server enforces
          // the two on POST /pos/invoices/.../transfer-to-dues.
          setDueTransferFor(entry);
        }} />

      {/* ─── Voided invoices view · Part 11 ─── */}
      <VoidedInvoicesDialog
        open={voidedListOpen}
        voids={state.session.invoice_voids || []}
        replacements={state.session.invoice_replacements || {}}
        onOpenBill={(sourceOrderId) => {
          setVoidedListOpen(false);
          setVoidedBillOrderId(sourceOrderId);
        }}
        onClose={() => setVoidedListOpen(false)} />

      {/* Part 7.2 · viewable voided bill (reused BillPreviewDialog) */}
      <BillPreviewDialog
        open={!!voidedBillOrderId}
        state={state}
        outletName={outletName}
        voidedOrderId={voidedBillOrderId}
        voidRecord={(state.session.invoice_voids || []).find((v) => v.source_order_id === voidedBillOrderId) || null}
        onCancel={() => setVoidedBillOrderId(null)} />

      {/* ─── Aggregator inbox · Part 6 ─── */}
      <AggregatorInboxDialog
        open={aggregatorTrayOpen}
        orders={MOCK_AGGREGATOR_ORDERS}
        processed={state.session.aggregator_processed || {}}
        onAccept={(agg) => {
          dispatch({ type: ACT.AGGREGATOR_ACCEPTED, payload: { aggregator: agg } });
          setAggregatorTrayOpen(false);
        }}
        onReject={(agg) => setAggregatorRejectFor(agg)}
        onClose={() => setAggregatorTrayOpen(false)} />

      <AggregatorRejectDialog
        agg={aggregatorRejectFor}
        onCancel={() => setAggregatorRejectFor(null)}
        onConfirm={({ reason_code, note }) => {
          if (aggregatorRejectFor) {
            dispatch({ type: ACT.AGGREGATOR_REJECTED, payload: {
              aggregator_id: aggregatorRejectFor.id,
              reason_code, note,
            }});
          }
          setAggregatorRejectFor(null);
        }} />

      {/* ─── Sprint 3 · Business day · shift · reports · KDS · tokens ─── */}
      <BusinessDayOpenDialog
        open={bizOpenDlg}
        lastClosedDate={state.session.last_closed_business_date}
        onCancel={() => setBizOpenDlg(false)}
        onConfirm={({ business_date, is_reopen }) => {
          if (is_reopen) {
            // Sprint 4 correction 6 · surface the PIN dialog. Only on
            // successful auth do we dispatch BUSINESS_DAY_OPENED with
            // the reopen_override so the reducer records the audit entry.
            setBizOpenDlg(false);
            setReopenPinFor({ business_date, pendingPromptFor: bizPromptFor });
            return;
          }
          if (livePos) livePos.openBusinessDay(business_date);
          else dispatch({ type: ACT.BUSINESS_DAY_OPENED, payload: { business_date, opened_by: "system" } });
          setBizOpenDlg(false);
          if (bizPromptFor) {
            if (bizPromptFor.kind === "non_dine_in") {
              if (livePos?.isLive) livePos.openOrder({ order_type: bizPromptFor.type });
              else dispatch({ type: ACT.ORDER_OPENED, payload: { type: bizPromptFor.type } });
            } else if (bizPromptFor.kind === "table") {
              setOpenDialogFor(bizPromptFor.table);
            }
            setBizPromptFor(null);
          }
        }} />

      {/* Sprint 4 correction 6 · Manager PIN gate for a business-day reopen */}
      <SharedPinDialog
        open={!!reopenPinFor}
        action_label={reopenPinFor
          ? `Reopen closed business day · ${reopenPinFor.business_date} (last closed ${state.session.last_closed_business_date || "—"}). This will be recorded in the Exception Report.`
          : ""}
        reason_prompt="Reason for reopening"
        onCancel={() => setReopenPinFor(null)}
        onConfirm={(auth) => {
          const target = reopenPinFor;
          setReopenPinFor(null);
          if (!target) return;
          dispatch({ type: ACT.BUSINESS_DAY_OPENED, payload: {
            business_date: target.business_date,
            opened_by: "system",
            reopen_override: {
              authorised_by: auth.authorised_by,
              reason_code:   auth.reason_code || "REOPEN_CLOSED_DAY",
              at:            auth.at,
            },
          }});
          const pending = target.pendingPromptFor;
          if (pending) {
            setBizPromptFor(null);
            setTimeout(() => {
              if (pending.kind === "non_dine_in") {
                if (livePos?.isLive) livePos.openOrder({ order_type: pending.type });
                else dispatch({ type: ACT.ORDER_OPENED, payload: { type: pending.type } });
              } else if (pending.kind === "table") {
                setOpenDialogFor(pending.table);
              }
            }, 20);
          }
        }} />

      <BusinessDayCloseDialog
        open={bizCloseDlg}
        state={state}
        livePos={livePos}
        onCancel={() => setBizCloseDlg(false)}
        onConfirm={() => {
          // POS Cleanup Pass 3 · Close Day now HANDS OFF to
          // TillShiftCloseFlow. Any open tills are walked through
          // first; only when every till is closed does the
          // orchestrator's `onShiftClosed` fire, which then chains
          // closeShift → closeBusinessDay → openBusinessDay(next) → Z.
          setBizCloseDlg(false);
          setDayCloseFlowOpen(true);
        }} />

      {/* Financial Ground Truth (Feb 2026) · Manager PIN gate for
          Move-to-Outstanding-Dues. Fired from the day-close blocker's
          per-row action; forwards to the server transfer-to-dues
          endpoint on success. */}
      <SharedPinDialog
        open={!!dueTransferFor}
        action_label={dueTransferFor
          ? `Move invoice ${dueTransferFor.invoice_no || dueTransferFor.order_id} to Outstanding Dues · the sale (₹${Number(dueTransferFor.total || 0).toFixed(2)}) stays on the books; cash is not being received today.`
          : ""}
        reason_prompt="Reason for moving to dues (required)"
        onCancel={() => setDueTransferFor(null)}
        onConfirm={async (auth) => {
          const target = dueTransferFor;
          setDueTransferFor(null);
          if (!target || !livePos) return;
          await livePos.transferInvoiceToDues({
            order_id:               target.order_id,
            invoice_no:             target.invoice_no,
            reason_type:            "uncollected",
            reason_note:            auth.reason_code || "Moved at business-day close",
            authorised_by_staff_id: auth.authorised_by_id || auth.authorised_by,
          });
        }} />

      {/* POS Cleanup Pass 3 · Close Day orchestrator. Mounts on top of
          the terminal when the manager taps Close Day (and no
          unsettled orders remain). Walks each open till through the
          same denomination flow used for self-close, then aggregates
          the closed-till ledger for the shift-close summary. On
          `onShiftClosed` we ask the server to (a) close the internal
          shift, (b) close the business day, (c) open the
          backend-resolved next_business_date so a browser refresh
          lands on the rolled-forward date. The Z-report is generated
          from the snapshot after the day rolls. */}
      {dayCloseFlowOpen && (
        <TillShiftCloseFlow
          open={true}
          currentStaffId={state?.session?.logged_in_staff_id || state?.session?.staff?.id || null}
          shift={currentShift}
          livePos={livePos}
          outletName={outletName}
          requestAuth={requestAuth}
          onCancel={() => setDayCloseFlowOpen(false)}
          onShiftClosed={async ({ closedTills, totals }) => {
            // POS Cleanup Pass 3 · Close Day chain — every hop is
            // server-authoritative; the reducer only mirrors the
            // outcome so the UI matches what the backend just wrote.
            const closedShift = currentShift;
            // 1) Close the internal shift on the server.
            if (livePos) {
              const r = await livePos.closeShift();
              if (!r?.ok) return; // toast already shown by the thunk
            }
            // Sync the reducer so `session.shifts` no longer flags the
            // just-closed shift as open (the BUSINESS_DAY_CLOSED
            // reducer case refuses to advance while an open shift
            // sits on the ledger).
            if (closedShift?.id) {
              dispatch({ type: ACT.SHIFT_CLOSED, payload: {
                shift_id:       closedShift.id,
                counted_cash:   totals.counted,
                expected_cash:  totals.expected,
                cash_variance:  totals.variance,
              }});
            }
            // 2) Close the business day on the server; capture the
            //    backend-resolved next_business_date (outlet-timezone
            //    aware).
            const closedBizDate = state.session.business_date;
            let next_business_date = null;
            if (livePos) {
              const r = await livePos.closeBusinessDay();
              if (!r?.ok) return;
              next_business_date = r.next_business_date;
            }
            // 3) Persist / open that next business date so
            //    /pos/session-status returns it after a browser
            //    refresh. Skip in demo (no livePos).
            if (livePos && next_business_date) {
              await livePos.openBusinessDay(next_business_date, "system");
            }
            // 4) Snapshot + Z-report entry. Bound to the day we just
            //    closed, not the newly-opened one.
            const snapshot = {
              at: Date.now(),
              generated_by: "system",
              business_date: closedBizDate,
              sales:       aggregateSales(state),
              settlements: aggregateSettlements(state),
              audit: {
                voids:         state.session.invoice_voids || [],
                cancellations: state.session.ticket_cancellations || [],
              },
              shift: {
                ...(closedShift || {}),
                counted_cash:  totals.counted,
                expected_cash: totals.expected,
                cash_variance: totals.variance,
                closed_tills:  closedTills,
              },
            };
            dispatch({ type: ACT.Z_REPORT_GENERATED, payload: {
              shift_id: closedShift?.id || null,
              generated_by: "system",
              snapshot,
            }});
            setDayCloseFlowOpen(false);
            setTimeout(() => {
              setZReportEntry({ z_number: "*", generated_by: "system", snapshot });
            }, 30);
          }} />
      )}

      {/* POS Control Sprint · shared PIN capture for the till-close
          orchestrator (and any future caller of requestAuth). One
          dialog, one authorising staff record — never re-mounted. */}
      <SharedPinDialog
        open={!!authRequest}
        action_label={authRequest?.reason || ""}
        onCancel={() => setAuthRequest(null)}
        onConfirm={(auth) => {
          const req = authRequest;
          setAuthRequest(null);
          req?.done?.(auth);
        }} />

      {xReportOpen && (
        <XReportDialog state={state}
          outletName={outletName}
          onClose={() => {
            const actor = state?.session?.staff_snapshot?.name
              || (state?.session?.logged_in_staff_id
                    ? (staffNameFor(state, state.session.logged_in_staff_id) || null)
                    : null)
              || userName
              || null;
            dispatch({ type: ACT.X_REPORT_TAKEN, payload: {
              // Sprint 2 Consolidation Part 7 · never hardcode "system"
              // on the report actor — that leaked as the printed value.
              taken_by: actor,
              shift_id: currentShift?.id || null,
              snapshot: {
                at: Date.now(),
                taken_by: actor,
                outlet_name: outletName,
                sales:       aggregateSales(state),
                settlements: aggregateSettlements(state),
                audit: {
                  voids:         state.session.invoice_voids || [],
                  cancellations: state.session.ticket_cancellations || [],
                },
                shift: currentShift,
              },
            }});
            setXReportOpen(false);
          }} />
      )}

      {zReportEntry && (
        <ZReportDialog state={state} zEntry={zReportEntry}
          onClose={() => setZReportEntry(null)} />
      )}

      {reportsHubOpen && (
        <ReportsHub state={state}
          onClose={() => setReportsHubOpen(false)}
          onOpenReport={(id) => { setReportsHubOpen(false); setOperationalReport(id); }} />
      )}
      {operationalReport === "exception"          && <ExceptionReport         state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "bill_register"     && <BillRegisterReport      state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "void_discount"     && <VoidDiscountAuditReport state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "item_sales"        && <ItemSalesReport         state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "hourly_sales"      && <HourlySalesReport       state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "tax_summary"       && <TaxSummaryReport        state={state} onClose={() => setOperationalReport(null)} />}
      {operationalReport === "settlement_summary" && <SettlementSummaryReport state={state} onClose={() => setOperationalReport(null)} />}

      {kdsOpen && POS_CONFIG.kds_enabled && (
        <KitchenDisplay state={state} dispatch={dispatch}
          onClose={() => setKdsOpen(false)} />
      )}

      {tokenQueueOpen && (
        <TokenQueue state={state} dispatch={dispatch}
          onClose={() => setTokenQueueOpen(false)} />
      )}

      {guestDisplayOpen && (
        <TokenQueue state={state} dispatch={dispatch} guestFacing={true}
          onClose={() => setGuestDisplayOpen(false)} />
      )}

      {/* Prompt dialog when user tries to order without a business day */}
      <BusinessDayOpenDialog
        open={!!bizPromptFor && !bizOpen && !bizOpenDlg}
        lastClosedDate={state.session.last_closed_business_date}
        onCancel={() => setBizPromptFor(null)}
        onConfirm={({ business_date, is_reopen }) => {
          const pending = bizPromptFor;
          if (is_reopen) {
            // Route through the PIN dialog exactly like the primary
            // open flow. The pending order kicks off after PIN success.
            setReopenPinFor({ business_date, pendingPromptFor: pending });
            setBizPromptFor(null);
            return;
          }
          dispatch({ type: ACT.BUSINESS_DAY_OPENED, payload: { business_date, opened_by: "system" } });
          setBizPromptFor(null);
          setTimeout(() => {
            if (pending?.kind === "non_dine_in") {
              if (livePos?.isLive) livePos.openOrder({ order_type: pending.type });
              else dispatch({ type: ACT.ORDER_OPENED, payload: { type: pending.type } });
            } else if (pending?.kind === "table") {
              setOpenDialogFor(pending.table);
            }
          }, 20);
        }} />
    </div>
  );
}

// ─── Table tile ─── Sprint 3 · flex-column layout · no absolute positioning
//   Row 1 (top): [cap · type] left  +  [status dot ● label] right, single line
//   Row 2:       [elapsed]    left  +  [steward initials]   right
//   Row 3 (bot): [covers]     left  +  [running total]      right
// Nothing overlaps: three deterministic rows, each with its own baseline.
function TableTile({ table, state, fullState, order, unavailReason, graceEntry, manageMode, onTap, onPointerDown, onPointerUp, onPointerLeave, totals }) {
  const cls = TILE_CLASS[state] || TILE_CLASS.VACANT;
  const dot = TILE_DOT[state] || TILE_DOT.VACANT;
  const now = Date.now();
  const graceRemaining = graceEntry ? Math.max(0, GRACE_MS - (now - graceEntry.at)) : 0;
  const openedAt = order?.order?.opened_at;
  const elapsedMin = openedAt ? Math.max(0, Math.floor((now - openedAt) / 60000)) : 0;
  const elapsedLabel = elapsedMin >= 60
    ? `${Math.floor(elapsedMin / 60)}h${(elapsedMin % 60).toString().padStart(2, "0")}`
    : `${elapsedMin}m`;
  const stewardName = order?.order?.steward_name
    || (order?.order?.steward_id ? staffNameFor(fullState, order.order.steward_id) : "");
  // Sprint 2 correction Part 4 · initials from RESOLVED NAME, never
  // from the raw ID. The previous implementation `steward_id.replace(/^s_?/i,"").slice(0,2)`
  // produced "40" from hex UUIDs starting with `40c9…`.
  const stewardInitials = stewardName && stewardName !== "—"
    ? stewardName.split(/\s+/).filter(Boolean).map((s) => s[0]).join("").slice(0, 2).toUpperCase()
    : "";
  const capOrType = table.is_temp ? "TEMP" : `Cap ${table.capacity}`;
  return (
    <button type="button" onClick={onTap}
      onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerLeave={onPointerLeave}
      data-testid={`pos-table-${table.id}`}
      data-table-state={state}
      title={
        manageMode ? "Tap to set availability"
        : state === "UNAVAILABLE" ? `Unavailable — ${unavailReason || ""}`
        : state === "GRACE" ? `Recently cleared · ${Math.ceil(graceRemaining / 1000)}s grace`
        : stewardName && stewardName !== "—" ? `Steward ${stewardName}`
        : "Long-press to mark unavailable"
      }
      style={{
        // Premium table cards — subtle 135° gradient (base → highlight)
        // per the approved legend. Vibrant but polished; the top-right
        // "highlight" corner keeps text luminous while the base tone
        // holds the operational identity. Border + box-shadow give the
        // "premium chip" feel with a soft frame that reads on every
        // background theme.
        backgroundImage: TILE_GRADIENT[state] || TILE_GRADIENT.VACANT,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
      }}
      className={`relative w-full h-full p-1.5 rounded-md text-left transition flex flex-col justify-between ${cls}
        ${table.is_temp ? "border-dashed border-2" : ""}
        ${manageMode ? "ring-2 ring-slate-400" : ""}`}>
      {/* Row 1 · top: capacity/type on the LEFT, [dot + label] on the RIGHT, single row.
          Text is forced to white via inline `color` because the parent's
          `text-white` class alone can't beat the descendants' prior
          token-based colour classes without a class-name change. */}
      <div className="flex items-center justify-between gap-1 leading-none" style={{ color: "#FFFFFF" }}>
        <span className="text-[10px] tabular-nums truncate opacity-90"
          data-testid={`pos-table-${table.id}-cap`}>
          {capOrType}
        </span>
        <span className="inline-flex items-center gap-1 shrink-0">
          {/* Status dot · Part 1.1 · 16px */}
          <span className={`w-4 h-4 rounded-full ${dot}`}
            data-testid={`pos-table-${table.id}-dot`} />
          <span className="text-[15px] font-semibold tabular-nums"
            data-testid={`pos-table-${table.id}-label`}>
            {table.label}
          </span>
        </span>
      </div>

      {order && (
        <>
          {/* Row 2 · middle · elapsed + steward */}
          <div className="flex items-center justify-between text-[10px] tabular-nums"
            style={{ color: "#FFFFFF" }}
            data-testid={`pos-table-${table.id}-elapsed-row`}>
            <span className="opacity-90" data-testid={`pos-table-${table.id}-elapsed`}>
              {elapsedLabel}
            </span>
          </div>
          {/* Row 3 · bottom · covers + running total */}
          <div className="flex items-center justify-between" style={{ color: "#FFFFFF" }}>
            <span className="text-[10px] tabular-nums opacity-90"
              data-testid={`pos-table-${table.id}-covers`}>
              {order.order.covers ? `${order.order.covers}c` : ""}
            </span>
            <span className="text-[11px] font-semibold tabular-nums"
              data-testid={`pos-table-${table.id}-total`}>
              ₹{fmt(totals?.grand_total || 0)}
            </span>
          </div>
        </>
      )}
      {!order && (
        // Push the top row to top and leave the rest empty for a calm vacant tile.
        <div />
      )}

      {state === "UNAVAILABLE" && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 text-[9px] uppercase tracking-wider font-semibold text-slate-700 rounded-md"
          data-testid={`pos-table-${table.id}-unavail-badge`}>
          Unavailable
        </div>
      )}
      {order?.order?.state === "HELD" && (
        <span className="absolute top-1.5 left-1.5 text-[7px] uppercase font-semibold bg-sky-600 text-white rounded px-1 leading-tight">
          H
        </span>
      )}
    </button>
  );
}

// Tile classes — no border here (border now applied inline for the
// uniform premium chip look). Text stays pure white with medium
// weight so labels remain crisp on every gradient in the legend.
const TILE_CLASS = {
  VACANT:      "text-white font-medium hover:brightness-110",
  OCCUPIED:    "text-white font-medium hover:brightness-110",
  BILLED:      "text-white font-medium hover:brightness-110",
  GRACE:       "text-white font-medium hover:brightness-110",
  UNAVAILABLE: "text-white font-medium cursor-not-allowed",
};

// Premium status gradient — vibrant 135° base → highlight per the
// approved legend. Values are hard-coded because these colours belong
// to the operational status contract — they are DELIBERATELY NOT
// exposed to the theme editor.
const TILE_GRADIENT = {
  VACANT:      "linear-gradient(135deg, #006D44 0%, #008A56 100%)",
  OCCUPIED:    "linear-gradient(135deg, #F29913 0%, #FFB84D 100%)",
  BILLED:      "linear-gradient(135deg, #9132E1 0%, #AF5CFF 100%)",
  GRACE:       "linear-gradient(135deg, #64C4ED 0%, #95DFFF 100%)",
  UNAVAILABLE: "linear-gradient(135deg, #D61A3C 0%, #FF4D6D 100%)",
};
// Status indicator — subtle pure-white LED at 20 % opacity for every
// state. Colour identity lives in the gradient; the dot is only an
// unobtrusive accent so it never clashes with the base tone.
const TILE_DOT = {
  VACANT:      "bg-white/20",
  OCCUPIED:    "bg-white/20",
  BILLED:      "bg-white/20",
  GRACE:       "bg-white/20",
  UNAVAILABLE: "bg-white/20",
};

function StateLegend() {
  return (
    <div className="hidden md:flex items-center gap-3 text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]"
      data-testid="pos-floor-legend">
      <LegendDot cls="bg-emerald-700" label="Vacant" />
      <LegendDot cls="bg-amber-500" label="Occupied" />
      <LegendDot cls="bg-purple-600" label="Billed" />
      <LegendDot cls="bg-sky-400 opacity-70" label="Grace" />
      <LegendDot cls="bg-rose-600" label="Unavailable" />
    </div>
  );
}
function LegendDot({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-3 h-3 rounded-full ${cls}`} />
      {label}
    </span>
  );
}

function NonDineInBtn({ label, icon: Icon, onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-[color:var(--color-primary)] bg-white text-[color:var(--color-primary)] text-[12px] font-medium hover:bg-[color:var(--color-primary)] hover:text-white transition">
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

// ─── Table-open dialog · Part 5.1 · phone-first with mock lookup ────
function TableOpenDialog({ table, onCancel, onConfirm }) {
  const [phone,  setPhone]  = useState("");
  const [name,   setName]   = useState("");
  const [covers, setCovers] = useState("");
  const [focus,  setFocus]  = useState("phone");
  const [prefill, setPrefill] = useState(false); // returning guest hit
  const [consent, setConsent] = useState(true);  // Part 5.3 consent flag
  // Sprint 8 · POS-QCS · elevation surface for covers > 999.
  const [coversElevation, setCoversElevation] = useState(null);
  const bank = useCustomerBank();

  // Part 5.1 · phone-first lookup via the consolidated useCustomerBank
  // hook (POS Control Sprint · Cleanup #2). Trigger when the number is
  // 10 digits — the mock ledger uses 10-digit keys. Everything else is
  // treated as a new guest. When the live customer bank endpoint lands,
  // this behaviour extends without touching call sites.
  useEffect(() => {
    const digits = normalisePhone(phone);
    if (digits.length !== 10) { setPrefill(false); return; }
    const g = bank.lookup(digits);
    if (g) {
      setPrefill(true);
      // Only pre-fill if the field is empty so the cashier can
      // override manually.
      setName((v) => v ? v : g.name);
    } else {
      setPrefill(false);
    }
  }, [phone, bank]);

  const submit = (authorisation_id = null) => {
    const n = parseInt(covers, 10);
    if (!(n > 0)) return;
    // Sprint 8 · POS-QCS · client-side guardrails matching the server.
    // Hard-block AND manager-elevation both route through the same
    // dialog so the UX is identical to the qty flow — the dialog's
    // internal `qty-elevation-hard-block` panel handles the >10,000 case.
    if (n > 999 && !authorisation_id) {
      if (coversElevation) return; // dialog already open
      setCoversElevation({
        intendedValue: n,
        previousValue: null,
      });
      return;
    }
    const digits = normalisePhone(phone);
    // Fire-and-forget customer bank upsert (no-op in mock mode); routes
    // through the same hook so every capture site converges on one
    // upstream customer master when the live endpoint lands.
    if (digits) bank.upsert({ phone: digits, name: name.trim() || null, tags: ["dine-in"] });
    onConfirm({
      covers: n,
      guest_name:  name.trim() || null,
      guest_phone: digits || null,
      consent_captured_at: (digits || name.trim()) ? Date.now() : null,
      authorisation_id: authorisation_id || null,
    });
  };

  const onCoversKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
    if (e.key === "Enter")  { e.preventDefault(); submit(); return; }
    if (e.key === "Backspace") { e.preventDefault(); setCovers((v) => v.slice(0, -1)); return; }
    // Sprint 8 · POS-QCS · allow up to 5 digits (max 10,000) — the
    // submit path enforces the actual limit and opens elevation for
    // 1,000..10,000. This ALSO stops a stuck key from ever accepting
    // a phone-number-length paste, because the input cap is 5.
    if (/^[0-9]$/.test(e.key)) { e.preventDefault(); setCovers((v) => (v + e.key).slice(0, 5)); }
  };

  useEffect(() => {
    const h = (e) => {
      if (e.target?.tagName === "INPUT") return;
      if (e.key === "Escape") { e.preventDefault(); onCancel(); return; }
      if (e.key === "Enter")  { e.preventDefault(); submit(); return; }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, name, covers, focus]);

  const onKeypad = (k) => {
    if (k === "Backspace") setCovers((v) => v.slice(0, -1));
    else if (k === "Escape") setCovers("");
    else if (k === "Enter") submit();
    // Sprint 8 · POS-QCS · widened to 5 digits (see onCoversKey).
    else if (/^[0-9]$/.test(k)) setCovers((v) => (v + k).slice(0, 5));
  };

  return (
    <Dialog open={!!table} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[560px] max-h-[520px] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] overflow-hidden flex flex-col"
        data-testid="pos-table-open-dialog">
        <DialogHeader className="px-4 py-2 border-b border-[color:var(--pos-color-border,#E2E8DF)] shrink-0 space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-normal">Open table</DialogTitle>
          <DialogDescription className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {table.label} <span className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] font-normal">· capacity {table.capacity}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 py-2 space-y-2 flex-1 overflow-y-auto">
          {/* Part 5.1 · Phone FIRST (was second). Lookup happens on 10
              digits and pre-fills the name for returning guests. */}
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Guest phone</label>
            <input type="tel" value={phone} autoFocus
              onFocus={() => setFocus("phone")}
              onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, ""))}
              inputMode="numeric"
              placeholder="10-digit phone (try 9820011122)"
              className={`w-full h-11 rounded border text-[15px] tabular-nums px-3 focus:outline-none
                ${prefill ? "border-emerald-500 bg-emerald-50" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white focus:border-[color:var(--pos-color-accent,var(--color-primary))]"}`}
              data-testid="pos-table-open-phone" />
            {prefill && (
              <div className="text-[10px] text-emerald-800 mt-0.5" data-testid="pos-table-open-prefill">
                Returning guest — name pre-filled.
              </div>
            )}
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Guest name (optional)</label>
            <input type="text" value={name}
              onFocus={() => setFocus("name")}
              onChange={(e) => setName(e.target.value)}
              placeholder="Guest name"
              className="w-full h-9 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[12px] px-2 focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
              data-testid="pos-table-open-name" />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]">Covers · required</label>
            <input type="text" value={covers} inputMode="none"
              onFocus={() => setFocus("covers")} onChange={() => {}}
              onKeyDown={onCoversKey}
              placeholder="—"
              className={`w-full h-11 rounded border text-[22px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums px-3 focus:outline-none
                ${focus === "covers" ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.04]" : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white"}`}
              data-testid="pos-table-open-covers" />
          </div>

          {focus === "covers" && (
            <MiniKeypad onKey={onKeypad} />
          )}

          {/* Part 5.3 · consent marker (structure only). Toggle shown
              whenever guest details are captured; a timestamp lands
              on the order at submit. No policy text; the field just
              exists so consent handling is not retrofitted later. */}
          <label className="flex items-start gap-2 text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] cursor-pointer select-none"
            data-testid="pos-table-open-consent">
            <input type="checkbox" checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="pos-table-open-consent-checkbox"
              className="mt-0.5 accent-[color:var(--color-primary)]" />
            <span>Guest agrees these details are stored for order and service purposes.</span>
          </label>
        </div>

        <div className="px-3 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={onCancel}
            data-testid="pos-table-open-cancel"
            className="h-10 px-3 rounded text-[11px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)] transition">
            Cancel · Esc
          </button>
          <button type="button" onClick={() => submit()}
            disabled={!(parseInt(covers, 10) > 0)}
            data-testid="pos-table-open-confirm"
            className="h-10 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition">
            Open · Enter
          </button>
        </div>
      </DialogContent>

      {/* Sprint 8 · POS-QCS · manager PIN when covers > 999 */}
      <QuantityElevationDialog
        open={!!coversElevation}
        kind="covers"
        intendedValue={coversElevation?.intendedValue}
        previousValue={coversElevation?.previousValue}
        order_id="__PENDING__"
        line_id={null}
        initiator_staff_id={null}
        API={process.env.REACT_APP_BACKEND_URL}
        headers={(() => {
          const posToken = typeof window !== "undefined"
            ? window.localStorage.getItem("pos_token") : null;
          let terminal_id = "unbound";
          try {
            const b = JSON.parse(window.localStorage.getItem("pos_terminal_binding") || "null");
            if (b?.terminal_id) terminal_id = b.terminal_id;
          } catch (_) { /* keep default */ }
          return {
            "Content-Type": "application/json",
            "X-Pos-Terminal": terminal_id,
            ...(posToken ? { Authorization: `Bearer ${posToken}` } : {}),
          };
        })()}
        onCancel={() => setCoversElevation(null)}
        onConfirm={(auth) => {
          setCoversElevation(null);
          submit(auth.authorisation_id);
        }} />
    </Dialog>
  );
}

function MiniKeypad({ onKey }) {
  const cell = "h-9 rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[16px] font-medium text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] active:bg-[#F0F0EE] select-none";
  // Item 1 (Sprint 3 corrections) · dedicated primary keycap so the
  // Enter key doesn't inherit `bg-white text-[color:var(--pos-color-primary-text,#1A2421)]` from `cell`.
  const primaryCell = "h-9 rounded border border-transparent bg-[color:var(--color-primary)] text-white text-[16px] font-medium tabular-nums hover:bg-[color:var(--color-primary-hover)] select-none";
  const send = (k) => () => onKey(k);
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="pos-table-open-keypad">
      {["1","2","3","4","5","6","7","8","9"].map((d) => (
        <button key={d} type="button" onClick={send(d)} className={cell} data-testid={`pos-table-open-key-${d}`}>{d}</button>
      ))}
      <button type="button" onClick={send("Backspace")} className={cell + " text-[#7A2E1F]"} data-testid="pos-table-open-key-back">←</button>
      <button type="button" onClick={send("0")} className={cell} data-testid="pos-table-open-key-0">0</button>
      <button type="button" onClick={send("Enter")} className={primaryCell} data-testid="pos-table-open-key-enter">↵</button>
    </div>
  );
}

// ─── Unavailable dialog · Part 3+5 ───────────────────────────────────
// Preset buttons plus free-text field, per Part 5. Recorded reason
// carries reason_code (from preset) + note (free text). Free-text-only
// entries are recorded as OTHER.
function UnavailableDialog({ table, currentReason, onCancel, onMark, onRestore }) {
  const [reason, setReason] = useState({ reason_code: null, note: "" });
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      if (e.key === "Enter" && reasonValid(reason)) {
        e.preventDefault();
        const n = normaliseReason(reason);
        onMark(n);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [reason, onCancel, onMark]);
  return (
    <Dialog open={!!table} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[540px] p-0 gap-0 border border-slate-200"
        data-testid="pos-unavailable-dialog">
        <DialogHeader className="px-5 py-3 border-b border-slate-100 bg-slate-50 space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {table?.label} · availability
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-700">
            {currentReason
              ? <>Currently unavailable — <strong>{currentReason}</strong></>
              : "Mark this table unavailable — pick a reason preset or type your own."}
          </DialogDescription>
        </DialogHeader>
        <div className="p-4">
          <ReasonInput
            presets={UNAVAILABLE_PRESETS}
            value={reason}
            onChange={setReason}
            testidPrefix="pos-unavailable" />
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-between gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-unavailable-cancel"
            className="h-11 px-3 rounded text-[11px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel · Esc
          </button>
          <div className="flex items-center gap-2">
            {currentReason && (
              <button type="button" onClick={onRestore}
                data-testid="pos-unavailable-restore"
                className="h-11 px-5 rounded bg-emerald-700 text-white text-[13px] font-semibold hover:bg-emerald-800 inline-flex items-center gap-1.5">
                Make available
              </button>
            )}
            <button type="button"
              onClick={() => reasonValid(reason) && onMark(normaliseReason(reason))}
              disabled={!reasonValid(reason)}
              data-testid="pos-unavailable-mark"
              className={`h-11 px-4 rounded text-[12px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed
                ${currentReason
                  ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "bg-slate-700 text-white hover:bg-slate-800"}`}>
              {currentReason ? "Update reason" : "Mark unavailable · Enter"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Temp table dialog · Part 9 ─────────────────────────────────────
function TempTableDialog({ open, onCancel, onCreate }) {
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setLabel("");
    // Focus after mount so the physical keyboard flows in.
    setTimeout(() => inputRef.current?.focus(), 20);
    const h = (e) => {
      // Item 7 (Sprint 3 corrections) · window listener MUST NOT
      // intercept typing keys. Physical text characters route to the
      // focused input via React's controlled-input onChange; the
      // window listener only handles Escape (and only when the input
      // does NOT have focus). Enter is handled by the input's own
      // onKeyDown so it can call e.preventDefault reliably.
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onCancel]);

  // Item 7 · in-app keyboard bridge. AlphaKeyboard emits synthetic
  // KeyboardEvent-shaped payloads; applyKeyToValue mirrors physical
  // typing so both paths mutate `label` identically.
  const onKeypad = ({ key }) => {
    if (key === "Enter")  { onCreate(label.trim()); return; }
    if (key === "Escape") { onCancel(); return; }
    setLabel((v) => applyKeyToValue(v, key));
    // Keep visible focus on the input so it looks active.
    inputRef.current?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-[560px] w-[92vw] max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col" data-testid="pos-temp-table-dialog">
        <DialogHeader className="px-5 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] space-y-0 shrink-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">New temporary table</DialogTitle>
          <DialogDescription className="text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
            For bar counters, events, outdoor service. Disappears from the floor once settled.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Label (optional)</div>
            <input ref={inputRef} type="text" value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                // Stop propagation so nothing outside the input can
                // eat physical keystrokes.
                e.stopPropagation();
                if (e.key === "Enter")  { e.preventDefault(); onCreate(label.trim()); }
                if (e.key === "Escape") { e.preventDefault(); onCancel(); }
              }}
              placeholder="e.g. Counter 1 · Lawn 3 · Bar 2"
              className="w-full h-11 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[13px] focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
              data-testid="pos-temp-label" />
            <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-1 italic">
              Leave blank and one is generated (like &quot;Temp 3&quot;).
            </div>
          </div>
          {/* Item 7 · in-app keyboard for touch-only terminals */}
          <AlphaKeyboard onKey={onKeypad} className="mt-2" />
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={onCancel}
            data-testid="pos-temp-cancel"
            className="h-11 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">Cancel</button>
          <button type="button" onClick={() => onCreate(label.trim())}
            data-testid="pos-temp-create"
            className="h-11 px-4 rounded bg-[color:var(--color-primary)] text-white text-[12px] font-semibold hover:bg-[color:var(--color-primary-hover)]">
            Create temp table · Enter
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Held-orders tray · Task 2 ──────────────────────────────────────
function HeldOrdersTray({ held, onClose, onResume }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md w-[92vw] max-h-[92vh] p-0 gap-0 border border-[color:var(--pos-color-border,#E2E8DF)] flex flex-col overflow-hidden"
        data-testid="pos-held-tray">
        <DialogHeader className="px-4 py-3 border-b border-[color:var(--pos-color-border,#E2E8DF)] flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="text-[10px] uppercase tracking-wider text-[color:var(--pos-color-secondary-text,#5C6B64)] font-normal">Held orders</DialogTitle>
            <DialogDescription className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
              {held.length} on hold
            </DialogDescription>
          </div>
          <button type="button" onClick={onClose}
            className="h-8 px-2.5 rounded border border-[color:var(--pos-color-border,#E2E8DF)] text-[11px]"
            data-testid="pos-held-tray-close">Close · Esc</button>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {held.length === 0 && (
            <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-8">
              No orders on hold. Use the Hold action from any OPEN order to park it here.
            </div>
          )}
          {held.map((o) => (
            <HeldEntry key={o.order.id} slice={o} onResume={() => onResume(o.order.id)} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HeldEntry({ slice, onResume }) {
  const total = (slice.lines || []).reduce((s, l) => s + l.qty * l.unit_price, 0);
  const held_min = slice.order.held_at ? Math.max(0, Math.floor((Date.now() - slice.order.held_at) / 60000)) : 0;
  const identifier = slice.order.type === ORDER_TYPE.DINE_IN
    ? (TABLES.find((t) => t.id === slice.order.table_id)?.label || slice.order.table_id || "—")
    : (slice.order.token || "—");
  return (
    <button type="button" onClick={onResume}
      data-testid={`pos-held-entry-${slice.order.id}`}
      className="w-full text-left rounded border border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] p-3 transition">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-sky-800 font-medium">
          {slice.order.type.replace("_", " ")}
        </div>
        <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">Held {held_min}m ago</div>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
          {identifier}
        </div>
        <div className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
          ₹{fmt(total)}
        </div>
      </div>
      <div className="mt-0.5 text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">
        {slice.lines.length} line{slice.lines.length === 1 ? "" : "s"} · tap to resume
      </div>
    </button>
  );
}

// ─── Aggregator inbox · Part 6 ──────────────────────────────────────
function AggregatorInboxDialog({ open, orders, processed, onAccept, onReject, onClose }) {
  const pending = orders.filter((o) => !processed[o.id]);
  const now = Date.now();
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[820px] w-[92vw] max-h-[92vh] p-0 gap-0 border border-orange-200 overflow-hidden flex flex-col"
        data-testid="pos-aggregator-inbox">
        <DialogHeader className="px-5 py-3 border-b border-orange-100 bg-orange-50 space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-orange-900 font-normal">Incoming orders</DialogTitle>
          <DialogDescription className="text-[15px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {pending.length} awaiting acceptance
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {pending.length === 0 && (
            <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-8" data-testid="pos-aggregator-empty">
              No incoming orders.
            </div>
          )}
          {pending.map((o) => {
            const age = now - o.received_at;
            const escalated = age > AGGREGATOR_ESCALATION_MS;
            const platformCls = o.platform === "zomato"
              ? "border-rose-300 bg-white"
              : "border-orange-300 bg-white";
            return (
              <div key={o.id}
                className={`rounded border p-3 ${platformCls} ${escalated ? "ring-2 ring-rose-500" : ""}`}
                data-testid={`pos-aggregator-row-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider
                      ${o.platform === "zomato" ? "bg-rose-600 text-white" : "bg-orange-600 text-white"}`}
                      data-testid={`pos-aggregator-row-${o.id}-platform`}>
                      {o.platform}
                    </span>
                    <span className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums"
                      data-testid={`pos-aggregator-row-${o.id}-ref`}>
                      {o.platform_ref}
                    </span>
                    {escalated && (
                      <span className="text-[10px] uppercase font-semibold text-rose-800 tabular-nums"
                        data-testid={`pos-aggregator-row-${o.id}-escalated`}>
                        · Escalated · {Math.floor(age / 60000)} min old
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">
                    ₹{Number(o.total_hint || 0).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)]">
                  {o.guest.name} · <span className="tabular-nums">{o.guest.phone}</span>
                </div>
                <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">{o.guest.address}</div>
                <div className="mt-1 text-[11px] text-[color:var(--pos-color-primary-text,#1A2421)]">
                  {o.items.map((it, i) => (
                    <span key={i} className="tabular-nums">
                      {it.qty}× {it.item_id.replace(/^i_/, "").replace(/_/g, " ")}
                      {i < o.items.length - 1 ? "  •  " : ""}
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button type="button" onClick={() => onReject(o)}
                    data-testid={`pos-aggregator-reject-${o.id}`}
                    className="h-8 px-3 rounded border border-rose-300 text-rose-800 text-[11px] font-medium hover:bg-rose-50">
                    Reject
                  </button>
                  <button type="button" onClick={() => onAccept(o)}
                    data-testid={`pos-aggregator-accept-${o.id}`}
                    className="h-8 px-3 rounded bg-emerald-700 text-white text-[11px] font-semibold hover:bg-emerald-800">
                    Accept · fire tickets
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end">
          <button type="button" onClick={onClose}
            data-testid="pos-aggregator-close"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const AGG_REJECT_PRESETS = [
  { code: "OUT_OF_STOCK",       label: "Out of stock" },
  { code: "KITCHEN_CAPACITY",   label: "Kitchen at capacity" },
  { code: "OUTSIDE_HOURS",      label: "Outside service hours" },
  { code: "ADDRESS_OUT_OF_RANGE", label: "Address out of range" },
  { code: "OTHER",              label: "Other" },
];

function AggregatorRejectDialog({ agg, onCancel, onConfirm }) {
  const [reasonCode, setReasonCode] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (agg) { setReasonCode(null); setNote(""); } }, [agg?.id]);
  if (!agg) return null;
  const valid = !!reasonCode;
  return (
    <Dialog open={!!agg} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md p-0 gap-0 border border-rose-200 overflow-hidden"
        data-testid="pos-aggregator-reject-dialog">
        <DialogHeader className="px-5 py-3 border-b border-rose-100 bg-rose-50 space-y-0">
          <DialogTitle className="text-[10px] uppercase tracking-wider text-rose-900 font-normal">Reject aggregator order</DialogTitle>
          <DialogDescription className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            {agg.platform} · {agg.platform_ref}
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)] mb-1">Reason · required</div>
            <div className="grid grid-cols-2 gap-1.5">
              {AGG_REJECT_PRESETS.map((r) => (
                <button key={r.code} type="button"
                  onClick={() => setReasonCode(r.code)}
                  data-testid={`pos-aggregator-reject-reason-${r.code}`}
                  className={`h-10 px-2 rounded border text-[11px] font-medium transition
                    ${reasonCode === r.code
                      ? "border-rose-500 bg-rose-50 text-rose-900"
                      : "border-[color:var(--pos-color-border,#E2E8DF)] bg-white text-[color:var(--pos-color-primary-text,#1A2421)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="w-full h-10 rounded border border-[color:var(--pos-color-border,#E2E8DF)] px-3 text-[12px] focus:border-[color:var(--pos-color-accent,var(--color-primary))] focus:outline-none"
            data-testid="pos-aggregator-reject-note" />
        </div>
        <div className="p-3 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel}
            data-testid="pos-aggregator-reject-cancel"
            className="h-10 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Cancel
          </button>
          <button type="button" onClick={() => onConfirm({ reason_code: reasonCode, note })}
            disabled={!valid}
            data-testid="pos-aggregator-reject-confirm"
            className="h-10 px-4 rounded bg-rose-700 text-white text-[12px] font-semibold hover:bg-rose-800 disabled:opacity-40">
            Reject order
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
// Lists every INVOICE_VOIDED event in the current session. Each row
// surfaces the voided number, the manager, the reason, the timestamp,
// and any replacement invoice number(s) once BILL_PRINTED runs on the
// reopened order.
function VoidedInvoicesDialog({ open, voids, replacements, onOpenBill, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[620px] w-[92vw] max-h-[560px] p-0 gap-0 border border-rose-200 overflow-hidden flex flex-col"
        data-testid="pos-voided-invoices-dialog">
        <DialogHeader className="px-5 py-3 border-b border-rose-100 bg-rose-50 space-y-0">
          <DialogTitle className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">
            Voided invoices · this business day
          </DialogTitle>
          <DialogDescription className="text-[11px] text-rose-900">
            Every allocated number resolves either to an invoice or to a recorded void — no gaps.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {voids.length === 0 && (
            <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-8" data-testid="pos-voided-empty">
              No invoices voided today.
            </div>
          )}
          {voids.map((v, i) => {
            const kids = Array.isArray(replacements[v.voided_invoice]) ? replacements[v.voided_invoice] : [];
            const at = new Date(v.at);
            return (
              <div key={`${v.voided_invoice || i}-${v.at}`}
                className="rounded border border-rose-200 bg-white p-3"
                data-testid={`pos-voided-row-${v.voided_invoice}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
                    {v.voided_invoice || "(no invoice)"}
                  </div>
                  <div className="text-[10px] text-[color:var(--pos-color-secondary-text,#5C6B64)] tabular-nums">
                    {at.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 text-[11px]">
                  <div><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Reason</span> <strong className="text-[color:var(--pos-color-primary-text,#1A2421)]">{v.reason_code || "—"}</strong></div>
                  <div className="text-right"><span className="text-[color:var(--pos-color-secondary-text,#5C6B64)]">Authorised by</span> <strong className="text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">{v.authorised_by}</strong></div>
                  {v.note && (
                    <div className="col-span-2 text-[10px] italic text-[color:var(--pos-color-secondary-text,#5C6B64)] mt-0.5">&ldquo;{v.note}&rdquo;</div>
                  )}
                  {kids.length > 0 && (
                    <div className="col-span-2 text-[11px] text-amber-800 mt-1"
                      data-testid={`pos-voided-replaced-by-${v.voided_invoice}`}>
                      Replaced by <strong className="tabular-nums">{kids.join(", ")}</strong>
                    </div>
                  )}
                </div>
                {/* Part 7.2 · view the voided bill itself */}
                {onOpenBill && v.source_order_id && (
                  <div className="mt-2 flex items-center justify-end">
                    <button type="button"
                      onClick={() => onOpenBill(v.source_order_id)}
                      data-testid={`pos-voided-view-bill-${v.voided_invoice || v.source_order_id}`}
                      className="h-8 px-3 rounded border border-rose-300 text-rose-900 text-[11px] font-medium hover:bg-rose-50">
                      View voided bill
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-[color:var(--pos-color-border,#E2E8DF)] flex items-center justify-end">
          <button type="button" onClick={onClose}
            data-testid="pos-voided-close"
            className="h-9 px-3 rounded text-[12px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)]">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
